import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import ObjectObservable from "../utils/ObjectObservable";
import CallData from "../classes/CallData";
import User from "../models/User";
import LeadUtils from "../classes/LeadUtils";
import PaginationView from "./PaginationView";
import Query from "../classes/Query";
import MessageDispatch from "../rocket/MessageDispatch";
import Button from "./Button";
import ListWrapper from "../utils/ListWrapper";
import CallHistoriesRequest from "../models/CallHistoriesRequest";
import RecentCallListTile from "./RecentCallListTile";
import TwilioClient from "../classes/TwilioClient";
import TextView from "./TextView";
import Interaction from "../models/Interaction";
import CollectionUtils from "../utils/CollectionUtils";
import CallHistories from "../classes/CallHistories";
import { UsageConstants } from "../rocket/D3ETemplate";
import { BuildContext } from "../classes/BuildContext";

type _AllOnPressed = (d3eState: CallsHistoryPageRefs) => void;

type _MissedOnPressed = (d3eState: CallsHistoryPageRefs) => void;

type _IncomingOnPressed = (d3eState: CallsHistoryPageRefs) => void;

type _OutgoingOnPressed = (d3eState: CallsHistoryPageRefs) => void;

type _VoicemailOnPressed = (d3eState: CallsHistoryPageRefs) => void;

export interface CallsHistoryPageProps extends BaseUIProps {
  key?: string;
  handlingUser: User;
  client: TwilioClient;
  data: CallData;
}
/// To store state data for CallsHistoryPage
class CallsHistoryPageRefs {
  public all: AllState = new AllState();
  public incoming: IncomingState = new IncomingState();
  listViewController: ui.ScrollController = new ui.ScrollController();
  public missed: MissedState = new MissedState();
  public outgoing: OutgoingState = new OutgoingState();
  public voicemail: VoicemailState = new VoicemailState();
}

interface VoicemailWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: CallsHistoryPageRefs;
  _onVoicemailButtonPressed?: _VoicemailOnPressed;
  filterType: string;
}

class VoicemailState extends ObjectObservable {
  private _disable: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
}

class _VoicemailWithState extends ObservableComponent<VoicemailWithStateProps> {
  voicemailFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: VoicemailWithStateProps) {
    super(props);

    this.initState();
  }
  public get filterType(): string {
    return this.props.filterType;
  }
  public get voicemail(): VoicemailState {
    return this.props.d3eState.voicemail;
  }
  public get d3eState(): CallsHistoryPageRefs {
    return this.props.d3eState;
  }
  public get _onVoicemailButtonPressed(): _VoicemailOnPressed {
    return this.props._onVoicemailButtonPressed;
  }
  public initState() {
    super.initState();

    this.updateObservable("voicemail", null, this.voicemail);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(["filterType", "voicemail"], this.rebuild);
  }
  public dispose(): void {
    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return Button({
      decoration:
        this.filterType === "Voicemail"
          ? new ui.BoxDecoration({
              border: ui.Border.all({ color: cStyle.c1, width: 1.0 }),
              borderRadius: ui.BorderRadius.circular(8.0),
              color: cStyle.c1,
            })
          : new ui.BoxDecoration({
              border: ui.Border.all({ color: cStyle.c19, width: 1.0 }),
              borderRadius: ui.BorderRadius.circular(8.0),
              color: cStyle.c12,
            }),
      disable: this.voicemail.disable,
      onPressed: () => {
        this._onVoicemailButtonPressed(this.d3eState);
      },
      onFocusChange: (val) => {},
      child: TextView({
        data: "Voicemail",
        states: ui.joinStates(
          { "data-c0": this.filterType === "Voicemail" },
          {}
        ),
        style: new ui.TextStyle({
          color: this.filterType === "Voicemail" ? cStyle.c14 : null,
          fontWeight:
            this.filterType === "Voicemail" ? ui.FontWeight.bold : null,
        }),
        className: "xa00 vc",
      }),
      className: "vc",
    });
  }
}
function VoicemailWithState(props: VoicemailWithStateProps) {
  return React.createElement(_VoicemailWithState, props);
}

interface OutgoingWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: CallsHistoryPageRefs;
  _onOutgoingButtonPressed?: _OutgoingOnPressed;
  filterType: string;
}

class OutgoingState extends ObjectObservable {
  private _disable: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
}

class _OutgoingWithState extends ObservableComponent<OutgoingWithStateProps> {
  outgoingFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: OutgoingWithStateProps) {
    super(props);

    this.initState();
  }
  public get filterType(): string {
    return this.props.filterType;
  }
  public get outgoing(): OutgoingState {
    return this.props.d3eState.outgoing;
  }
  public get d3eState(): CallsHistoryPageRefs {
    return this.props.d3eState;
  }
  public get _onOutgoingButtonPressed(): _OutgoingOnPressed {
    return this.props._onOutgoingButtonPressed;
  }
  public initState() {
    super.initState();

    this.updateObservable("outgoing", null, this.outgoing);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(["filterType", "outgoing"], this.rebuild);
  }
  public dispose(): void {
    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return Button({
      decoration:
        this.filterType === "Outgoing"
          ? new ui.BoxDecoration({
              border: ui.Border.all({ color: cStyle.c1, width: 1.0 }),
              borderRadius: ui.BorderRadius.circular(8.0),
              color: cStyle.c1,
            })
          : new ui.BoxDecoration({
              border: ui.Border.all({ color: cStyle.c19, width: 1.0 }),
              borderRadius: ui.BorderRadius.circular(8.0),
              color: cStyle.c12,
            }),
      disable: this.outgoing.disable,
      onPressed: () => {
        this._onOutgoingButtonPressed(this.d3eState);
      },
      onFocusChange: (val) => {},
      child: TextView({
        data: "Outgoing",
        states: ui.joinStates(
          { "data-c0": this.filterType === "Outgoing" },
          {}
        ),
        style: new ui.TextStyle({
          color: this.filterType === "Outgoing" ? cStyle.c14 : null,
          fontWeight:
            this.filterType === "Outgoing" ? ui.FontWeight.bold : null,
        }),
        className: "xcd6 vc",
      }),
      className: "vc",
    });
  }
}
function OutgoingWithState(props: OutgoingWithStateProps) {
  return React.createElement(_OutgoingWithState, props);
}

interface IncomingWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: CallsHistoryPageRefs;
  _onIncomingButtonPressed?: _IncomingOnPressed;
  filterType: string;
}

class IncomingState extends ObjectObservable {
  private _disable: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
}

class _IncomingWithState extends ObservableComponent<IncomingWithStateProps> {
  incomingFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: IncomingWithStateProps) {
    super(props);

    this.initState();
  }
  public get filterType(): string {
    return this.props.filterType;
  }
  public get incoming(): IncomingState {
    return this.props.d3eState.incoming;
  }
  public get d3eState(): CallsHistoryPageRefs {
    return this.props.d3eState;
  }
  public get _onIncomingButtonPressed(): _IncomingOnPressed {
    return this.props._onIncomingButtonPressed;
  }
  public initState() {
    super.initState();

    this.updateObservable("incoming", null, this.incoming);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(["filterType", "incoming"], this.rebuild);
  }
  public dispose(): void {
    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return Button({
      decoration:
        this.filterType === "Incoming"
          ? new ui.BoxDecoration({
              border: ui.Border.all({ color: cStyle.c1, width: 1.0 }),
              borderRadius: ui.BorderRadius.circular(8.0),
              color: cStyle.c1,
            })
          : new ui.BoxDecoration({
              border: ui.Border.all({ color: cStyle.c19, width: 1.0 }),
              borderRadius: ui.BorderRadius.circular(8.0),
              color: cStyle.c12,
            }),
      disable: this.incoming.disable,
      onPressed: () => {
        this._onIncomingButtonPressed(this.d3eState);
      },
      onFocusChange: (val) => {},
      child: TextView({
        data: "Incoming",
        states: ui.joinStates(
          { "data-c0": this.filterType === "Incoming" },
          {}
        ),
        style: new ui.TextStyle({
          color: this.filterType === "Incoming" ? cStyle.c14 : null,
          fontWeight:
            this.filterType === "Incoming" ? ui.FontWeight.bold : null,
        }),
        className: "x731 vc",
      }),
      className: "vc",
    });
  }
}
function IncomingWithState(props: IncomingWithStateProps) {
  return React.createElement(_IncomingWithState, props);
}

interface MissedWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: CallsHistoryPageRefs;
  _onMissedButtonPressed?: _MissedOnPressed;
  filterType: string;
}

class MissedState extends ObjectObservable {
  private _disable: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
}

class _MissedWithState extends ObservableComponent<MissedWithStateProps> {
  missedFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: MissedWithStateProps) {
    super(props);

    this.initState();
  }
  public get filterType(): string {
    return this.props.filterType;
  }
  public get missed(): MissedState {
    return this.props.d3eState.missed;
  }
  public get d3eState(): CallsHistoryPageRefs {
    return this.props.d3eState;
  }
  public get _onMissedButtonPressed(): _MissedOnPressed {
    return this.props._onMissedButtonPressed;
  }
  public initState() {
    super.initState();

    this.updateObservable("missed", null, this.missed);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(["filterType", "missed"], this.rebuild);
  }
  public dispose(): void {
    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return Button({
      decoration:
        this.filterType === "Missed"
          ? new ui.BoxDecoration({
              border: ui.Border.all({ color: cStyle.c1, width: 1.0 }),
              borderRadius: ui.BorderRadius.circular(8.0),
              color: cStyle.c1,
            })
          : new ui.BoxDecoration({
              border: ui.Border.all({ color: cStyle.c19, width: 1.0 }),
              borderRadius: ui.BorderRadius.circular(8.0),
              color: cStyle.c12,
            }),
      disable: this.missed.disable,
      onPressed: () => {
        this._onMissedButtonPressed(this.d3eState);
      },
      onFocusChange: (val) => {},
      child: TextView({
        data: "Missed",
        states: ui.joinStates({ "data-c0": this.filterType === "Missed" }, {}),
        style: new ui.TextStyle({
          color: this.filterType === "Missed" ? cStyle.c14 : null,
          fontWeight: this.filterType === "Missed" ? ui.FontWeight.bold : null,
        }),
        className: "xbb2 vc",
      }),
      className: "vc",
    });
  }
}
function MissedWithState(props: MissedWithStateProps) {
  return React.createElement(_MissedWithState, props);
}

interface AllWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: CallsHistoryPageRefs;
  _onAllButtonPressed?: _AllOnPressed;
  filterType: string;
}

class AllState extends ObjectObservable {
  private _disable: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
}

class _AllWithState extends ObservableComponent<AllWithStateProps> {
  allFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: AllWithStateProps) {
    super(props);

    this.initState();
  }
  public get all(): AllState {
    return this.props.d3eState.all;
  }
  public get filterType(): string {
    return this.props.filterType;
  }
  public get d3eState(): CallsHistoryPageRefs {
    return this.props.d3eState;
  }
  public get _onAllButtonPressed(): _AllOnPressed {
    return this.props._onAllButtonPressed;
  }
  public initState() {
    super.initState();

    this.updateObservable("all", null, this.all);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(["all", "filterType"], this.rebuild);
  }
  public dispose(): void {
    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return Button({
      decoration:
        this.filterType === "All"
          ? new ui.BoxDecoration({
              border: ui.Border.all({ color: cStyle.c1, width: 1.0 }),
              borderRadius: ui.BorderRadius.circular(8.0),
              color: cStyle.c1,
            })
          : new ui.BoxDecoration({
              border: ui.Border.all({ color: cStyle.c19, width: 1.0 }),
              borderRadius: ui.BorderRadius.circular(8.0),
              color: cStyle.c12,
            }),
      disable: this.all.disable,
      onPressed: () => {
        this._onAllButtonPressed(this.d3eState);
      },
      onFocusChange: (val) => {},
      child: TextView({
        data: "All",
        states: ui.joinStates({ "data-c0": this.filterType === "All" }, {}),
        style: new ui.TextStyle({
          color: this.filterType === "All" ? cStyle.c14 : null,
          fontWeight: this.filterType === "All" ? ui.FontWeight.bold : null,
        }),
        className: "x048 vc",
      }),
      className: "vc",
    });
  }
}
function AllWithState(props: AllWithStateProps) {
  return React.createElement(_AllWithState, props);
}

class _CallsHistoryPageState extends ObservableComponent<CallsHistoryPageProps> {
  static defaultProps = { handlingUser: null, client: null, data: null };
  d3eState: CallsHistoryPageRefs = new CallsHistoryPageRefs();
  query: CallHistories = null;
  request: CallHistoriesRequest = null;
  filterType: string = "";
  histories: Array<Interaction> = ListWrapper.widget(this, "histories");
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: CallsHistoryPageProps) {
    super(props);

    this.initState();
  }
  public get handlingUser(): User {
    return this.props.handlingUser;
  }
  public get client(): TwilioClient {
    return this.props.client;
  }
  public get data(): CallData {
    return this.props.data;
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;

    this.onInit();
  }
  public initListeners(): void {
    this.updateSyncProperty("handlingUser", this.props.handlingUser);

    this.on(
      ["request", "request.offset", "request.pageSize", "request.user"],
      this.computeQuery
    );

    this.computeQuery();

    this.on(
      [
        "filterType",
        "query",
        "query.items",
        "query.items.callStatus",
        "query.items.isIncoming",
        "query.items.type",
      ],
      this.computeHistories
    );

    this.computeHistories();

    this.updateSyncProperty("data", this.props.data);

    this.on(
      [
        "client",
        "data",
        "filterType",
        "handlingUser",
        "histories",
        "query",
        "query.count",
        "request",
        "request.offset",
        "request.pageSize",
      ],
      this.rebuild
    );
  }
  public componentDidUpdate(prevProps: CallsHistoryPageProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.handlingUser !== this.props.handlingUser) {
      this.updateObservable(
        "handlingUser",
        prevProps.handlingUser,
        this.props.handlingUser
      );

      this.fire("handlingUser", this);
    }

    if (prevProps.client !== this.props.client) {
      this.fire("client", this);
    }

    if (prevProps.data !== this.props.data) {
      this.updateObservable("data", prevProps.data, this.props.data);

      this.fire("data", this);
    }
  }
  public setQuery(val: CallHistories): void {
    let isValChanged: boolean = this.query !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("query", this.query, val);

    MessageDispatch.get().dispose(this.query);

    this.query = val;

    this.fire("query", this);
  }
  public computeQuery = async (): Promise<void> => {
    try {
      this.setQuery(
        await Query.get().getCallHistories(
          UsageConstants.QUERY_GETCALLHISTORIES_CALLSHISTORYPAGE_PROPERTIES_QUERY_COMPUTATION,
          this.request,
          { "synchronize": true }
        )
      );
    } catch (exception) {
      console.log(" exception in computeQuery : " + exception.toString());

      this.setQuery(null);
    }
  };
  public setRequest(val: CallHistoriesRequest): void {
    let isValChanged: boolean = this.request !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("request", this.request, val);

    this.request = val;

    this.fire("request", this);
  }
  public setFilterType(val: string): void {
    let isValChanged: boolean = this.filterType !== val;

    if (!isValChanged) {
      return;
    }

    this.filterType = val;

    this.fire("filterType", this);
  }
  public setHistories(val: Array<Interaction>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(
      this.histories,
      val
    );

    if (!isValChanged) {
      return;
    }

    this.updateObservableColl("histories", this.histories, val);

    this.histories.clear();

    this.histories.addAll(val);

    this.fire("histories", this);
  }
  public addToHistories(val: Interaction, index: number = -1): void {
    if (index === -1) {
      if (!this.histories.contains(val)) this.histories.add(val);
    } else {
      this.histories.remove(this.histories.elementAt(index));

      this.histories.add(val);
    }

    this.fire("histories", this, val, true);

    this.updateObservable("histories", null, val);
  }
  public removeFromHistories(val: Interaction): void {
    this.histories.remove(val);

    this.fire("histories", this, val, false);

    this.removeObservable("histories", val);
  }
  public computeHistories = (): void => {
    try {
      this.setHistories(
        Array.from(
          LeadUtils.getInteractionsByType(
            this.query?.items ?? [],
            this.filterType
          )
        )
      );
    } catch (exception) {
      console.log(" exception in computeHistories : " + exception.toString());

      this.setHistories([]);
    }
  };
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Column({
      children: [
        ui.Row({
          children: [
            TextView({
              data: "History",
              style: new ui.TextStyle({ fontWeight: ui.FontWeight.bold }),
              className: "x2f9 vc",
              key: "0",
            }),
            ui.Container({ className: "x19a hc vc h", key: "1" }),
            ui.Row({
              children: [
                AllWithState({
                  d3eState: this.d3eState,
                  _onAllButtonPressed: this.onAllButtonPressed,
                  filterType: this.filterType,
                  key: "0",
                }),
                ui.Container({ className: "xa95 hc vc", key: "1" }),
                MissedWithState({
                  d3eState: this.d3eState,
                  _onMissedButtonPressed: this.onMissedButtonPressed,
                  filterType: this.filterType,
                  key: "2",
                }),
                ui.Container({ className: "xd18 hc vc", key: "3" }),
                IncomingWithState({
                  d3eState: this.d3eState,
                  _onIncomingButtonPressed: this.onIncomingButtonPressed,
                  filterType: this.filterType,
                  key: "4",
                }),
                ui.Container({ className: "x1fe hc vc", key: "5" }),
                OutgoingWithState({
                  d3eState: this.d3eState,
                  _onOutgoingButtonPressed: this.onOutgoingButtonPressed,
                  filterType: this.filterType,
                  key: "6",
                }),
                ui.Container({ className: "x3ea hc vc", key: "7" }),
                VoicemailWithState({
                  d3eState: this.d3eState,
                  _onVoicemailButtonPressed: this.onVoicemailButtonPressed,
                  filterType: this.filterType,
                  key: "8",
                }),
              ],
              className: "x1a5 vc",
              key: "2",
            }),
          ],
          className: "xd5 hc vc h",
          key: "0",
        }),
        ui.Row({
          children: [ui.Container({ className: "x91 hc vc h", key: "0" })],
          className: "x046 hc h",
          key: "1",
        }),
        ui.ListView({
          controller: this.d3eState.listViewController,
          children: [
            this.histories.expand((interaction) => [
              RecentCallListTile({
                interaction: interaction,
                client: this.client,
                data: this.data,
                currentUser: this.handlingUser,
                className: "x5acc hc h",
                key: interaction?.ident,
              }),
            ]),
          ],
          className: "x9d0 hc vc h v",
          key: "2",
        }),
        ui.Row({
          mainAxisAlignment: ui.MainAxisAlignment.end,
          children: [
            PaginationView({
              pageSize: this.request.pageSize,
              total: this.query !== null ? this.query.count : 0,
              offset: this.request.offset,
              onChange: (pageSize, offset) => {
                this.onChangePaginationViewHandler(
                  pageSize,
                  offset,
                  this.d3eState
                );
              },
              key: "0",
            }),
          ],
          className: "x64cb hc h",
          key: "3",
        }),
      ],
      className: ui.join("CallsHistoryPage x4fc hc vc ", this.props.className),
      ...copyBaseUIProps(this.props),
    });
  }
  public onInit = (): void => {
    this.setFilterType("All");

    this.setRequest(
      new CallHistoriesRequest({
        user: this.handlingUser,
        pageSize: 100,
        offset: 0,
      })
    );
  };
  public onAllButtonPressed = (d3eState: CallsHistoryPageRefs): void => {
    this.setFilterType("All");
  };
  public onMissedButtonPressed = (d3eState: CallsHistoryPageRefs): void => {
    this.setFilterType("Missed");
  };
  public onIncomingButtonPressed = (d3eState: CallsHistoryPageRefs): void => {
    this.setFilterType("Incoming");
  };
  public onOutgoingButtonPressed = (d3eState: CallsHistoryPageRefs): void => {
    this.setFilterType("Outgoing");
  };
  public onVoicemailButtonPressed = (d3eState: CallsHistoryPageRefs): void => {
    this.setFilterType("Voicemail");
  };
  public onChangePaginationViewHandler = (
    pageSize: number,
    offset: number,
    d3eState: CallsHistoryPageRefs
  ): void => {
    this.request.setPageSize(pageSize);

    this.request.setOffset(offset);

    this.setRequest(this.request.deepClone());
  };
  public dispose(): void {
    MessageDispatch.get().dispose(this.query);

    super.dispose();
  }
  public get all() {
    return this.d3eState.all;
  }
  public get incoming() {
    return this.d3eState.incoming;
  }
  public get missed() {
    return this.d3eState.missed;
  }
  public get outgoing() {
    return this.d3eState.outgoing;
  }
  public get voicemail() {
    return this.d3eState.voicemail;
  }
}
export default function CallsHistoryPage(props: CallsHistoryPageProps) {
  return React.createElement(_CallsHistoryPageState, {
    ..._CallsHistoryPageState.defaultProps,
    ...props,
  });
}
